import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { supabase } from '../supabaseClient';

const { FiUsers, FiMessageSquare, FiStar, FiPlus, FiCheck, FiTrendingUp, FiAward, FiX } = FiIcons;

const PeerReview = () => {
  const { session } = useAuth();
  const { data: profileData, refreshData } = useProfile();
  const isCohortMember = profileData?.user?.is_cohort_member || false;
  const [peerReviews, setPeerReviews] = useState([]);
  const [showAddReview, setShowAddReview] = useState(false);
  const [newReview, setNewReview] = useState({ reviewer: '', role: '', comment: '' });

  const fetchReviews = async () => {
    if (!session?.user?.id) return;
    const { data } = await supabase
      .from('peer_reviews')
      .select('*')
      .eq('profile_id', session.user.id)
      .order('created_at', { ascending: false });
    if (data) setPeerReviews(data);
  };

  useEffect(() => {
    fetchReviews();
  }, [session]);

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Program Launch Banner */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-8 rounded-3xl text-white shadow-xl relative overflow-hidden"
      >
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="max-w-xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-black uppercase tracking-widest">New Program</span>
            </div>
            <h1 className="text-4xl font-black mb-2">Peer Review Program 2.0 Launch</h1>
            <p className="text-blue-100 text-lg">Help others optimize their career paths and earn "Expert Credits" to unlock premium AI analysis on your own profile.</p>
          </div>
          <button
            disabled={isCohortMember}
            onClick={async () => {
              if (isCohortMember) return;
              const { error } = await supabase.from('profiles').update({ is_cohort_member: true }).eq('id', session.user.id);
              if (!error) refreshData();
            }}
            className={`${isCohortMember ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-blue-600 hover:bg-blue-50 shadow-lg'} px-8 py-4 rounded-2xl font-black transition-all flex items-center gap-2 whitespace-nowrap`}
          >
            {isCohortMember ? 'Joined Cohort' : 'Join the Cohort'} <SafeIcon icon={FiAward} />
          </button>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <SafeIcon icon={FiMessageSquare} className="text-blue-500" /> Feedback Insights
            </h2>
            <button onClick={() => setShowAddReview(true)} className="text-blue-600 text-sm font-bold flex items-center gap-1 hover:underline">
              <SafeIcon icon={FiPlus} /> Request New Review
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-6 rounded-2xl border border-gray-100">
              <p className="text-[10px] text-gray-400 font-black uppercase mb-4">Sentiment Analysis</p>
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-gray-100 h-2 rounded-full overflow-hidden">
                  <div className="bg-green-500 h-full w-[80%]"></div>
                </div>
                <span className="text-sm font-bold text-green-600">80% Positive</span>
              </div>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-gray-100">
              <p className="text-[10px] text-gray-400 font-black uppercase mb-4">Common Themes</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-lg border border-blue-100">#ImpactQuantification</span>
                <span className="px-2 py-1 bg-purple-50 text-purple-600 text-[10px] font-bold rounded-lg border border-purple-100">#CloudNative</span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {peerReviews.map((review, idx) => (
              <motion.div 
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-blue-100 transition-all"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 font-black text-lg">
                      {review.reviewer.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{review.reviewer}</h4>
                      <p className="text-xs text-gray-500">{review.role}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase ${
                      review.sentiment === 'Positive' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {review.sentiment}
                    </span>
                    <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase ${
                      review.status === 'Resolved' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {review.status}
                    </span>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed italic text-lg">"{review.comment}"</p>
                <div className="mt-6 pt-6 border-t border-gray-50 flex justify-between items-center">
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <SafeIcon icon={FiUsers} /> Received {new Date(review.created_at).toLocaleDateString()}
                  </span>
                  <div className="flex gap-4">
                    <button
                      onClick={async () => {
                        await supabase.from('peer_reviews').delete().eq('id', review.id);
                        fetchReviews();
                      }}
                      className="text-sm font-bold text-gray-400 hover:text-gray-600"
                    >
                      Dismiss
                    </button>
                    <button
                      onClick={async () => {
                        await supabase.from('peer_reviews').update({ status: 'Resolved' }).eq('id', review.id);
                        fetchReviews();
                      }}
                      className="text-sm font-bold text-blue-600 hover:text-blue-700"
                    >
                      Mark Resolved
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-900 text-white p-8 rounded-3xl shadow-xl">
            <h3 className="font-black text-xl mb-6 flex items-center gap-2">
              <SafeIcon icon={FiStar} className="text-yellow-400" /> Your Impact Score 
            </h3>
            <div className="space-y-6">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Community Trust</span>
                  <span className="text-green-400 font-black">Elite</span>
                </div>
                <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                  <div className="bg-green-500 h-full w-[92%]"></div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                <div>
                  <p className="text-[10px] text-gray-400 font-black uppercase">Reviews Given</p>
                  <p className="text-2xl font-black mt-1">28</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-black uppercase">Credits</p>
                  <p className="text-2xl font-black mt-1">1,240</p>
                </div>
              </div>
            </div>
            <button className="w-full mt-8 py-4 bg-white text-gray-900 rounded-2xl font-black text-sm hover:bg-blue-50 transition-all">
              Earn More Credits
            </button>
          </div>

          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
              <SafeIcon icon={FiAward} className="text-blue-500" /> Top Contributors
            </h3>
            <div className="space-y-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-full border border-gray-200"></div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900">Expert_{i*432}</p>
                    <p className="text-[10px] text-gray-500 font-medium">Sr. Architect @ Meta</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-black text-blue-600">{100-i*5} Reviews</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showAddReview && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowAddReview(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
              exit={{ opacity: 0, scale: 0.9, y: "-50%", x: "-50%" }}
              className="fixed left-1/2 top-1/2 w-[500px] bg-white rounded-3xl shadow-2xl z-[70] p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-gray-900 uppercase">Add External Feedback</h3>
                <button onClick={() => setShowAddReview(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <SafeIcon icon={FiX} className="text-gray-400" />
                </button>
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Reviewer Name (e.g. Jane Doe)"
                  value={newReview.reviewer}
                  onChange={(e) => setNewReview({...newReview, reviewer: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Reviewer Role (e.g. Senior PM @ TechCorp)"
                  value={newReview.role}
                  onChange={(e) => setNewReview({...newReview, role: e.target.value})}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500"
                />
                <textarea
                  placeholder="Review Comment"
                  value={newReview.comment}
                  onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                  rows={4}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none focus:border-blue-500 resize-none"
                />
              </div>
              <div className="mt-8 pt-8 border-t border-gray-50 flex gap-3">
                <button onClick={() => setShowAddReview(false)} className="flex-1 py-4 bg-gray-100 text-gray-500 rounded-2xl font-black text-xs uppercase hover:bg-gray-200 transition-all">
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!newReview.reviewer || !newReview.comment) return;
                    const { error } = await supabase
                      .from('peer_reviews')
                      .insert({
                        profile_id: session.user.id,
                        reviewer: newReview.reviewer,
                        role: newReview.role || 'Peer',
                        comment: newReview.comment,
                        sentiment: 'Positive',
                        status: 'Unresolved'
                      });
                    if (!error) {
                      setNewReview({ reviewer: '', role: '', comment: '' });
                      setShowAddReview(false);
                      fetchReviews();
                    }
                  }}
                  className="flex-1 py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25"
                >
                  Submit Mock Review
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PeerReview;